/**
 * client/render/modelmat.ts — BO2-lit material for dynamic models (player bodies, third-person weapons,
 * missiles, pickups). Works for skinned and rigid meshes. OWNER: RENDER agent.
 *
 * Lighting: col = albedo^2 * (ambient + sunColor * sunShadow * saturate(N.L) + dynamic light), fogged,
 * out = sqrt(col * exposure) (same output contract as the world, glsl.ts).
 * `ambient` stands in for the engine's light-grid probe of a moving model: the renderer sets it per
 * object from the nearest static-model irradiance samples (players.ts probeAmbient, ESTIMATE).
 * Normal maps use the derivative tangent frame (GLB bodies have no tangents).
 */
import * as THREE from 'three';
import { BO2_FRAG_COMMON, type Bo2Uniforms } from './glsl';

const MODEL_VERT = /* glsl */ `
#include <common>
#include <skinning_pars_vertex>
out vec3 vWorldPos;
out vec3 vNormal;
out vec2 vUv;
#ifdef USE_LAYERS
attribute float aLayer;
flat out float vLayer;
#endif
void main() {
#ifdef USE_LAYERS
  vLayer = aLayer;
#endif
  #include <skinbase_vertex>
  #include <beginnormal_vertex>
  #include <skinnormal_vertex>
  #include <begin_vertex>
  #include <skinning_vertex>
  vec4 wp = modelMatrix * vec4(transformed, 1.0);
  vWorldPos = wp.xyz;
  vNormal = normalize(mat3(modelMatrix) * objectNormal);
  vUv = uv;
  gl_Position = projectionMatrix * viewMatrix * wp;
}
`;

const MODEL_FRAG = /* glsl */ `
precision highp float;
${BO2_FRAG_COMMON}
#ifdef USE_LAYERS
precision highp sampler2DArray;
uniform sampler2DArray uMapArr;
uniform sampler2DArray uNormalArr;
flat in float vLayer;
#define SAMPLE_MAP() texture(uMapArr, vec3(vUv, vLayer))
#define SAMPLE_NORMAL() texture(uNormalArr, vec3(vUv, vLayer))
#else
uniform sampler2D uMap;
uniform sampler2D uNormalMap;
#define SAMPLE_MAP() texture(uMap, vUv)
#define SAMPLE_NORMAL() texture(uNormalMap, vUv)
#endif
uniform vec3 uAmbient;
uniform vec3 uTint;
uniform float uOpacity;
uniform float uAlphaCut;
uniform float uEmissive;     // 0 = lit, >0 = unlit glow (retrieve models), value = intensity
in vec3 vWorldPos;
in vec3 vNormal;
in vec2 vUv;
void main() {
  vec4 tex = SAMPLE_MAP();
#ifdef USE_ALPHATEST
  if (tex.a < uAlphaCut) discard;
#endif
  vec3 base = tex.rgb * uTint;
  base *= base;
  vec3 col;
  if (uEmissive > 0.0) {
    col = base * uEmissive / uExposure;
  } else {
    vec3 N = normalize(vNormal);
    if (!gl_FrontFacing) N = -N;
#ifdef USE_NORMALMAP
    vec3 mapN = SAMPLE_NORMAL().xyz * 2.0 - 1.0;
    N = bo2BumpNormal(N, vWorldPos, vUv, mapN);
#endif
    vec3 E = uAmbient;
    float ndl = max(dot(N, uSunDir), 0.0);
#ifdef RECEIVE_SUN
    if (ndl > 0.0) E += uSunColor * (ndl * bo2SunShadow(vWorldPos + normalize(vNormal) * 2.0, normalize(vNormal)));
#endif
    E += bo2DynLight(vWorldPos, N);
    col = base * E;
  }
  col = bo2Fog(col, vWorldPos);
  float a = tex.a * uOpacity;
#ifdef ADDITIVE
  vec4 o = bo2Out(col, 1.0);
  gl_FragColor = vec4(o.rgb * a, 1.0);
#else
  gl_FragColor = bo2Out(col, a);
#endif
}
`;

export interface ModelMaterialOptions {
  /** Texture-array variant (merged multi-material meshes, see mergeLayered()). */
  layers?: { color: THREE.DataArrayTexture; normal: THREE.DataArrayTexture | null };
  receiveSun?: boolean;
  additive?: boolean;
  emissive?: number;
}

export function makeBo2ModelMaterial(src: THREE.Material, U: Bo2Uniforms, o: ModelMaterialOptions = {}): THREE.ShaderMaterial {
  const s = src as THREE.MeshStandardMaterial;
  const defines: Record<string, string> = {};
  if (o.layers) { defines.USE_LAYERS = ''; if (o.layers.normal) defines.USE_NORMALMAP = ''; }
  else if (s.normalMap) defines.USE_NORMALMAP = '';
  if (s.alphaTest > 0) defines.USE_ALPHATEST = '';
  if (o.receiveSun !== false) defines.RECEIVE_SUN = '';
  if (o.additive) defines.ADDITIVE = '';
  for (const t of [s.map, s.normalMap]) if (t) t.colorSpace = THREE.NoColorSpace;
  if (s.map) s.map.needsUpdate = true;
  const tint = s.color ? new THREE.Vector3(Math.sqrt(s.color.r), Math.sqrt(s.color.g), Math.sqrt(s.color.b)) : new THREE.Vector3(1, 1, 1);
  const m = new THREE.ShaderMaterial({
    name: `bo2m_${s.name}`,
    defines,
    uniforms: {
      ...U,
      uMap: { value: s.map ?? WHITE_TEX },
      uMapArr: { value: o.layers?.color ?? null },
      uNormalArr: { value: o.layers?.normal ?? null },
      uNormalMap: { value: s.normalMap ?? null },
      uAmbient: { value: new THREE.Vector3(1, 1, 1) },
      uTint: { value: tint },
      uOpacity: { value: s.opacity ?? 1 },
      uAlphaCut: { value: s.alphaTest || 0.5 },
      uEmissive: { value: o.emissive ?? 0 },
    },
    vertexShader: MODEL_VERT,
    fragmentShader: MODEL_FRAG,
    side: s.side,
    transparent: s.transparent || !!o.additive,
    depthWrite: o.additive ? false : s.depthWrite,
  });
  if (o.additive) { m.blending = THREE.CustomBlending; m.blendSrc = THREE.OneFactor; m.blendDst = THREE.OneFactor; }
  return m;
}

const WHITE_TEX = (() => {
  const t = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
  t.needsUpdate = true;
  return t;
})();

/** Depth-only material for shadow proxies (skinned or rigid). */
export function makeShadowDepthMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    name: 'bo2_shadow_depth',
    vertexShader: /* glsl */ `
#include <common>
#include <skinning_pars_vertex>
void main() {
  #include <skinbase_vertex>
  #include <begin_vertex>
  #include <skinning_vertex>
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(transformed, 1.0);
}`,
    fragmentShader: /* glsl */ `void main() { gl_FragColor = vec4(1.0); }`,
    colorWrite: false,
    side: THREE.DoubleSide,
  });
}
